import { useEffect, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

// Vertex shader — passes UVs to fragment shader
const vertexShader = /* glsl */ `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

// Fragment shader — procedurally generates jet plume appearance
const fragmentShader = /* glsl */ `
  uniform float uTime;
  uniform vec3 uColorCore;
  uniform vec3 uColorMid;
  uniform vec3 uColorOuter;
  uniform float uPlumeIntensity;

  varying vec2 vUv;

  // Simple pseudo-random hash
  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  // Value noise
  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);

    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));

    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }

  // Fractal Brownian Motion for richer noise
  float fbm(vec2 p) {
    float value = 0.0;
    float amplitude = 0.5;
    for (int i = 0; i < 4; i++) {
      value += amplitude * noise(p);
      p *= 2.0;
      amplitude *= 0.5;
    }
    return value;
  }

  void main() {
    // UV axes: y = across width, x = along length
    float centerDist = abs(vUv.y - 0.5) * 2.0; // 0 at center, 1 at edges

    // Distance along plume: 0 = nozzle end, 1 = tip
    float along = vUv.x;

    // Conical radial falloff — wider near nozzle, narrower at tip
    float coneWidth = mix(1.0, 0.15, along);
    float radialFalloff = 1.0 - smoothstep(0.0, coneWidth, centerDist);

    // Longitudinal alpha falloff — solid near nozzle, fading at tip
    float lengthFalloff = 1.0 - smoothstep(0.0, 0.95, along);
    float nozzleBoost = smoothstep(0.3, 0.0, along) * 0.5;
    lengthFalloff += nozzleBoost;

    // Slight falloff at nozzle end to soften sharp edges
    float nozzleFade = smoothstep(0.0, 0.08, along);
    lengthFalloff *= nozzleFade;

    // Animated noise for flickering/turbulence
    float speed = uTime * 30.0;
    vec2 noiseCoord = vec2(vUv.y * 3.0, along * 6.0 - speed);
    float flicker = fbm(noiseCoord);
    float flicker2 = noise(vec2(vUv.y * 8.0, along * 12.0 - speed * 2.5));

    // Combine noise with falloff
    float noiseMod = mix(0.6, 1.0, flicker * 0.7 + flicker2 * 0.3);

    // Color gradient along plume
    vec3 color;
    if (along < 0.2) {
      color = mix(uColorCore, uColorMid, along / 0.2);
    } else if (along < 0.6) {
      float t = (along - 0.2) / 0.4;
      color = mix(uColorMid, uColorOuter, t);
    } else {
      color = uColorOuter;
    }

    // Brighten core (center of plane)
    float coreBrightness = smoothstep(0.5, 0.0, centerDist) * 1.5;
    color += vec3(coreBrightness * 0.3) * (1.0 - along * 0.8);

    // Final alpha
    float alpha = radialFalloff * lengthFalloff * noiseMod * uPlumeIntensity;
    alpha = clamp(alpha, 0.0, 1.0);

    // Boost color intensity for additive blending
    color *= (1.0 + coreBrightness * 0.5);

    gl_FragColor = vec4(color * alpha, alpha);
  }
`

export function Jet({ enableLight = false, lightIntensity = 0.1, plumeIntensity = 1.0, ...props }) {
  const shaderMaterial = useMemo(() => new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      uTime:           { value: 0 },
      uColorCore:      { value: new THREE.Color(1.0, 1.0, 0.85) },  // near-white hot core
      uColorMid:       { value: new THREE.Color(1.0, 0.38, 0.02) }, // deep orange
      uColorOuter:     { value: new THREE.Color(0.05, 0.2, 0.9) },  // vivid blue tip
      uPlumeIntensity: { value: plumeIntensity },
    },
    transparent: true,
    blending:   THREE.AdditiveBlending,
    side:       THREE.FrontSide,
    depthWrite: false,
  }), [])

  // Dispose shader material on unmount
  useEffect(() => {
    return () => {
      shaderMaterial.dispose()
    }
  }, [shaderMaterial])

  // Sync plumeIntensity prop changes
  useEffect(() => {
    shaderMaterial.uniforms.uPlumeIntensity.value = plumeIntensity
  }, [plumeIntensity, shaderMaterial])

  // Advance time uniform each frame
  useFrame((_, delta) => {
    shaderMaterial.uniforms.uTime.value += delta
  })

  return (
    <group {...props} dispose={null}>
      {enableLight && (
        <pointLight
          position={[0, 0, 0]}
          intensity={lightIntensity}
          distance={1}
          decay={4}
          color="#ff8844"
          castShadow={false}
        />
      )}

      {/* Inner group with pivot offset — matches SCRUB source layout */}
      <group position={[0.5, 0, 0]}>
        <mesh rotation={[0, 0, 0]}>
          <planeGeometry />
          <primitive object={shaderMaterial} attach="material" />
        </mesh>

        <mesh rotation={[Math.PI * 0.25, 0, 0]}>
          <planeGeometry />
          <primitive object={shaderMaterial} attach="material" />
        </mesh>

        <mesh rotation={[Math.PI * 0.5, 0, 0]}>
          <planeGeometry />
          <primitive object={shaderMaterial} attach="material" />
        </mesh>

        <mesh rotation={[Math.PI * 0.75, 0, 0]}>
          <planeGeometry />
          <primitive object={shaderMaterial} attach="material" />
        </mesh>

        <mesh rotation={[1, 0, 0]}>
          <planeGeometry />
          <primitive object={shaderMaterial} attach="material" />
        </mesh>

        <mesh rotation={[-Math.PI * 0.25, 0, 0]}>
          <planeGeometry />
          <primitive object={shaderMaterial} attach="material" />
        </mesh>

        <mesh rotation={[-Math.PI * 0.5, 0, 0]}>
          <planeGeometry />
          <primitive object={shaderMaterial} attach="material" />
        </mesh>

        <mesh rotation={[-Math.PI * 0.75, 0, 0]}>
          <planeGeometry />
          <primitive object={shaderMaterial} attach="material" />
        </mesh>
      </group>
    </group>
  )
}
